import React from 'react';
import { Box, Typography, ListItemButton } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import type { TransformedProtocol } from '@/app/queries/evaluationProtocolQueries';

interface ProtocolListItemProps {
  protocol: TransformedProtocol;
  isSelected: boolean;
  selectedCount: number;
  totalCount: number;
  onClick: (protocolId: number) => void;
}

const ProtocolListItem: React.FC<ProtocolListItemProps> = React.memo(({
  protocol,
  isSelected,
  selectedCount,
  totalCount,
  onClick
}) => {
  const handleClick = React.useCallback(() => {
    onClick(protocol.id);
  }, [protocol.id, onClick]);

  const hasSelections = selectedCount > 0;

  return (
    <ListItemButton
      onClick={handleClick}
      sx={{
        py: 1.5,
        px: 2,
        borderLeft: '4px solid',
        borderLeftColor: isSelected ? 'primary.main' : 'transparent',
        backgroundColor: isSelected ? 'primary.50' : 'transparent',
        '&:hover': {
          backgroundColor: isSelected ? 'primary.100' : 'action.hover',
        },
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: isSelected || hasSelections ? 600 : 400,
            color: isSelected ? 'primary.main' : 'text.primary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {protocol.title}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        {hasSelections && (
          <Typography
            variant="caption"
            sx={{
              backgroundColor: 'primary.main',
              color: 'white',
              px: 1,
              py: 0.25,
              borderRadius: 1,
              fontWeight: 600,
              fontSize: '0.75rem',
              whiteSpace: 'nowrap',
            }}
          >
            {selectedCount}/{totalCount}
          </Typography>
        )}
        <ChevronRightIcon
          sx={{
            color: isSelected ? 'primary.main' : 'action.active',
            fontSize: '1.25rem',
          }}
        />
      </Box>
    </ListItemButton>
  );
});

ProtocolListItem.displayName = 'ProtocolListItem';

export default ProtocolListItem;
